import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Upload, Save, Eye, CheckCircle, 
  Palette, FileText, QrCode, Settings, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/api';

// 8 Professional Templates (User Selected: 2,3,4,5,6,8,9,10)
const PROFESSIONAL_TEMPLATES = [
  {
    id: 'blue-curve',
    name: 'Blue Curve',
    orientation: 'vertical',
    previewColor: '#1e40af',
    gradient: 'linear-gradient(180deg, #1e40af 0%, #3b82f6 100%)',
    description: 'Curved Header Style'
  },
  {
    id: 'purple-magenta',
    name: 'Purple Magenta',
    orientation: 'vertical',
    previewColor: '#9333ea',
    gradient: 'linear-gradient(135deg, #9333ea 0%, #c026d3 50%, #e879f9 100%)',
    description: 'Wave Design'
  },
  {
    id: 'red-corporate',
    name: 'Red Corporate',
    orientation: 'vertical',
    previewColor: '#dc2626',
    gradient: '#dc2626',
    description: 'Circle Photo Style'
  },
  {
    id: 'navy-gold',
    name: 'Navy Gold Premium',
    orientation: 'vertical',
    previewColor: '#1e3a5f',
    gradient: 'linear-gradient(180deg, #1e3a5f 0%, #1e40af 100%)',
    accentColor: '#fbbf24',
    description: 'Premium Look'
  },
  {
    id: 'green-modern',
    name: 'Green Modern',
    orientation: 'vertical',
    previewColor: '#059669',
    gradient: 'linear-gradient(180deg, #064e3b 0%, #059669 100%)',
    accentColor: '#fbbf24',
    description: 'Corner Accent'
  },
  {
    id: 'maroon-classic',
    name: 'Maroon Classic',
    orientation: 'vertical',
    previewColor: '#7c2d12',
    gradient: '#7c2d12',
    description: 'Traditional Style'
  },
  {
    id: 'pink-gradient',
    name: 'Pink Gradient',
    orientation: 'vertical',
    previewColor: '#ec4899',
    gradient: 'linear-gradient(180deg, #be185d 0%, #ec4899 50%, #f9a8d4 100%)',
    description: 'Soft & Elegant'
  },
  {
    id: 'dark-premium',
    name: 'Dark Premium Gold',
    orientation: 'vertical',
    previewColor: '#18181b',
    gradient: 'linear-gradient(180deg, #18181b 0%, #27272a 100%)',
    accentColor: '#fbbf24',
    description: 'Luxury Design'
  }
];

// Template Preview Card Component
const TemplatePreviewCard = ({ template, isSelected, onClick, settings }) => {
  const renderMiniPreview = () => {
    const commonStyle = {
      width: '100%',
      height: '100%',
      borderRadius: '6px',
      overflow: 'hidden',
      position: 'relative',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '5px',
      color: 'white'
    };

    switch (template.id) {
      case 'blue-curve':
        return (
          <div style={{ ...commonStyle, background: template.gradient }}>
            <div style={{ height: '35%', position: 'relative' }}>
              <svg viewBox="0 0 100 20" style={{ position: 'absolute', bottom: '-1px', width: '100%' }}>
                <path d="M0,10 Q50,20 100,10 L100,20 L0,20 Z" fill="white"/>
              </svg>
            </div>
            <div style={{ background: 'white', height: '65%', padding: '8px' }}>
              <div style={{ width: '20px', height: '24px', background: '#e5e7eb', margin: '-12px auto 4px', border: '2px solid #1e40af' }}></div>
              <div style={{ height: '3px', background: '#1e293b', width: '60%', margin: '0 auto 3px', borderRadius: '2px' }}></div>
              <div style={{ height: '2px', background: '#e5e7eb', width: '80%', margin: '2px auto' }}></div>
              <div style={{ height: '2px', background: '#e5e7eb', width: '80%', margin: '2px auto' }}></div>
            </div>
          </div>
        );

      case 'purple-magenta':
        return (
          <div style={{ ...commonStyle, background: template.gradient }}>
            <div style={{ height: '32%', position: 'relative' }}>
              <svg viewBox="0 0 100 25" style={{ position: 'absolute', bottom: '-12px', width: '100%' }}>
                <path d="M0,12 Q50,25 100,12 L100,25 L0,25 Z" fill="white"/>
              </svg>
            </div>
            <div style={{ background: 'white', height: '68%', padding: '10px 6px 6px' }}>
              <div style={{ width: '22px', height: '26px', background: '#e5e7eb', margin: '0 auto 4px', border: '2px solid #9333ea' }}></div>
              <div style={{ height: '2px', background: '#e5e7eb', width: '90%', margin: '3px auto' }}></div>
              <div style={{ height: '2px', background: '#e5e7eb', width: '90%', margin: '2px auto' }}></div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '10%', background: 'linear-gradient(135deg, #9333ea, #c026d3)' }}></div>
          </div>
        );

      case 'red-corporate':
        return (
          <div style={{ ...commonStyle, background: 'white' }}>
            <div style={{ height: '25%', background: '#dc2626', clipPath: 'polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)' }}></div>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#e5e7eb', border: '2px solid #dc2626', margin: '-8px auto 4px' }}></div>
            <div style={{ height: '3px', background: '#1e293b', width: '50%', margin: '0 auto 3px', borderRadius: '2px' }}></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px', padding: '0 4px' }}>
              <div style={{ height: '12px', background: '#fef2f2', borderRadius: '2px' }}></div>
              <div style={{ height: '12px', background: '#fef2f2', borderRadius: '2px' }}></div>
              <div style={{ height: '12px', background: '#fef2f2', borderRadius: '2px' }}></div>
              <div style={{ height: '12px', background: '#fef2f2', borderRadius: '2px' }}></div>
            </div>
            <svg viewBox="0 0 100 15" style={{ position: 'absolute', bottom: 0, width: '100%' }}>
              <path d="M0,8 Q25,0 50,8 T100,8 L100,15 L0,15 Z" fill="#dc2626"/>
            </svg>
          </div>
        );

      case 'navy-gold':
        return (
          <div style={{ ...commonStyle, background: template.gradient, border: '1px solid #fbbf24' }}>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }}></div>
            <div style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ width: '14px', height: '14px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%', margin: '0 auto 2px', border: '1px solid #fbbf24' }}></div>
              <div style={{ height: '2px', background: 'white', width: '60%', margin: '0 auto', borderRadius: '2px' }}></div>
            </div>
            <div style={{ width: '20px', height: '24px', background: 'rgba(255,255,255,0.1)', border: '2px solid white', margin: '6px auto' }}></div>
            <div style={{ height: '2px', background: 'white', width: '50%', margin: '0 auto 2px', borderRadius: '2px' }}></div>
            <div style={{ background: '#fbbf24', padding: '2px', textAlign: 'center', width: '40%', margin: '0 auto', borderRadius: '6px' }}>
              <div style={{ height: '2px', background: '#1e3a5f', borderRadius: '2px' }}></div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fbbf24', height: '8%' }}></div>
          </div>
        );

      case 'green-modern':
        return (
          <div style={{ ...commonStyle, background: template.gradient }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: '20px', height: '20px', background: '#fbbf24', clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}></div>
            <div style={{ padding: '8px', textAlign: 'center' }}>
              <div style={{ width: '16px', height: '16px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%', margin: '0 auto 4px' }}></div>
              <div style={{ height: '2px', background: 'white', width: '60%', margin: '0 auto', borderRadius: '2px' }}></div>
            </div>
            <div style={{ width: '20px', height: '24px', background: 'rgba(255,255,255,0.1)', border: '2px solid #fbbf24', margin: '0 auto 4px' }}></div>
            <div style={{ height: '2px', background: 'white', width: '50%', margin: '0 auto 4px', borderRadius: '2px' }}></div>
            <div style={{ padding: '0 6px' }}>
              <div style={{ height: '2px', background: 'rgba(255,255,255,0.3)', margin: '3px 0', borderRadius: '2px' }}></div>
              <div style={{ height: '2px', background: 'rgba(255,255,255,0.3)', margin: '3px 0', borderRadius: '2px' }}></div>
            </div>
          </div>
        );

      case 'maroon-classic':
        return (
          <div style={{ ...commonStyle, background: 'white', border: '2px solid #7c2d12' }}>
            <div style={{ background: '#7c2d12', padding: '4px', textAlign: 'center' }}>
              <div style={{ height: '2px', background: 'white', width: '70%', margin: '0 auto', borderRadius: '2px' }}></div>
            </div>
            <div style={{ height: '3px', background: 'linear-gradient(90deg, #fbbf24, #7c2d12, #fbbf24)' }}></div>
            <div style={{ padding: '6px' }}>
              <div style={{ width: '22px', height: '26px', background: '#e5e7eb', border: '2px solid #7c2d12', margin: '0 auto 4px' }}></div>
              <div style={{ height: '3px', background: '#7c2d12', width: '50%', margin: '0 auto 4px', borderRadius: '2px' }}></div>
              <div style={{ height: '2px', background: '#e5e7eb', margin: '3px 0' }}></div>
              <div style={{ height: '2px', background: '#e5e7eb', margin: '3px 0' }}></div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: '-2px', right: '-2px', background: '#7c2d12', height: '8%' }}></div>
          </div>
        );

      case 'pink-gradient':
        return (
          <div style={{ ...commonStyle, background: template.gradient }}>
            <div style={{ padding: '6px', textAlign: 'center' }}>
              <div style={{ width: '14px', height: '14px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', margin: '0 auto 3px' }}></div>
              <div style={{ height: '2px', background: 'white', width: '70%', margin: '0 auto', borderRadius: '2px' }}></div>
            </div>
            <div style={{ width: '22px', height: '26px', background: 'rgba(255,255,255,0.15)', border: '2px solid white', borderRadius: '4px', margin: '0 auto 4px' }}></div>
            <div style={{ height: '2px', background: 'white', width: '50%', margin: '0 auto 4px', borderRadius: '2px' }}></div>
            <div style={{ background: 'rgba(255,255,255,0.15)', margin: '0 6px', padding: '6px', borderRadius: '4px' }}>
              <div style={{ height: '2px', background: 'rgba(255,255,255,0.5)', margin: '2px 0' }}></div>
              <div style={{ height: '2px', background: 'rgba(255,255,255,0.5)', margin: '2px 0' }}></div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.2)', height: '8%' }}></div>
          </div>
        );

      case 'dark-premium':
        return (
          <div style={{ ...commonStyle, background: template.gradient, border: '1px solid #fbbf24' }}>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }}></div>
            <div style={{ padding: '6px', textAlign: 'center' }}>
              <div style={{ height: '2px', background: '#fbbf24', width: '60%', margin: '0 auto', borderRadius: '2px' }}></div>
              <div style={{ height: '2px', background: 'rgba(255,255,255,0.5)', width: '40%', margin: '2px auto', borderRadius: '2px' }}></div>
            </div>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }}></div>
            <div style={{ width: '22px', height: '26px', background: 'rgba(255,255,255,0.05)', border: '1px solid #fbbf24', margin: '6px auto' }}></div>
            <div style={{ height: '2px', background: 'white', width: '50%', margin: '0 auto 2px', borderRadius: '2px' }}></div>
            <div style={{ height: '2px', background: '#fbbf24', width: '40%', margin: '0 auto', borderRadius: '2px' }}></div>
            <div style={{ padding: '0 6px', marginTop: '6px' }}>
              <div style={{ height: '2px', background: 'rgba(251,191,36,0.3)', margin: '3px 0' }}></div>
              <div style={{ height: '2px', background: 'rgba(251,191,36,0.3)', margin: '3px 0' }}></div>
            </div>
          </div>
        );

      default:
        return (
          <div style={{ ...commonStyle, background: template.gradient }}>
            <div style={{ height: '30%', background: 'rgba(0,0,0,0.1)' }}></div>
            <div style={{ background: 'white', height: '70%', padding: '8px' }}>
              <div style={{ width: '20px', height: '24px', background: '#e5e7eb', margin: '0 auto 4px' }}></div>
            </div>
          </div>
        );
    }
  };

  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
        isSelected
          ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      }`}
      data-testid={`template-card-${template.id}`}
    >
      {isSelected && (
        <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1 shadow-lg">
          <CheckCircle className="h-4 w-4 text-white" />
        </div>
      )}
      
      {/* Template Preview */}
      <div className="w-16 h-24 mx-auto mb-3 rounded-lg shadow-md overflow-hidden">
        {renderMiniPreview()}
      </div>
      
      <p className="text-sm font-semibold text-center text-gray-800">
        {template.name}
      </p>
      <p className="text-xs text-center text-gray-500">
        {template.description}
      </p>
    </div>
  );
};

// Live Preview Component - Shows actual template design
const LivePreview = ({ template, settings }) => {
  
  // Render template-specific front preview
  const renderFrontPreview = () => {
    const collegeName = settings.college_name || 'College Name';
    const collegeLogo = settings.college_logo;
    const principalSign = settings.principal_signature;
    const principalName = settings.principal_name || 'Principal';
    const emergencyContact = settings.emergency_contact || '1800-XXX-XXXX';
    
    switch(template) {
      case 'blue-curve':
        return (
          <div style={{ width: '192px', height: '288px', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui' }}>
            <div style={{ height: '80px', background: 'linear-gradient(180deg, #1e40af 0%, #3b82f6 100%)', position: 'relative', textAlign: 'center', padding: '8px' }}>
              {collegeLogo ? <img src={collegeLogo} style={{ height: '24px', borderRadius: '50%', background: 'white', padding: '2px' }} alt="" /> : <div style={{ width: '24px', height: '24px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', margin: '0 auto' }}></div>}
              <div style={{ color: 'white', fontWeight: '700', fontSize: '10px', marginTop: '4px' }}>{collegeName}</div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '6px' }}>(Govt. Recognised)</div>
              <svg viewBox="0 0 200 25" style={{ position: 'absolute', bottom: '-1px', left: 0, width: '100%' }}>
                <path d="M0,12 Q100,28 200,12 L200,25 L0,25 Z" fill="white"/>
              </svg>
            </div>
            <div style={{ marginTop: '-10px', position: 'relative', zIndex: 10, textAlign: 'center' }}>
              <div style={{ width: '50px', height: '62px', border: '3px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', background: '#f3f4f6', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: '#9ca3af' }}>Photo</div>
            </div>
            <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#1e293b', margin: '6px 0' }}>Student Name</div>
            <div style={{ padding: '0 12px', fontSize: '7px' }}>
              <div style={{ borderBottom: '1px solid #e5e7eb', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>Father's Name</span><span>Mr. XYZ</span></div>
              <div style={{ borderBottom: '1px solid #e5e7eb', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>D.O.B.</span><span>01/01/2000</span></div>
              <div style={{ borderBottom: '1px solid #e5e7eb', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>Course</span><span style={{ color: '#1e40af', fontWeight: '500' }}>B.Tech</span></div>
            </div>
            <div style={{ position: 'absolute', bottom: '6px', right: '10px', textAlign: 'center' }}>
              {principalSign ? <img src={principalSign} style={{ height: '16px' }} alt="" /> : <div style={{ width: '40px', borderTop: '1px solid #333', marginBottom: '2px' }}></div>}
              <div style={{ fontSize: '5px', color: '#1e40af' }}>{principalName}</div>
            </div>
          </div>
        );

      case 'purple-magenta':
        return (
          <div style={{ width: '192px', height: '288px', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui' }}>
            <div style={{ height: '85px', background: 'linear-gradient(135deg, #9333ea 0%, #c026d3 50%, #e879f9 100%)', position: 'relative', textAlign: 'center', padding: '10px' }}>
              {collegeLogo ? <img src={collegeLogo} style={{ height: '24px', borderRadius: '50%' }} alt="" /> : <div style={{ width: '24px', height: '24px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', margin: '0 auto' }}></div>}
              <div style={{ color: 'white', fontSize: '10px', fontWeight: '700', marginTop: '4px' }}>{collegeName}</div>
              <svg viewBox="0 0 200 30" style={{ position: 'absolute', bottom: '-15px', left: 0, width: '100%' }}>
                <path d="M0,15 Q100,35 200,15 L200,30 L0,30 Z" fill="white"/>
              </svg>
            </div>
            <div style={{ position: 'relative', zIndex: 10, marginTop: '-18px', textAlign: 'center' }}>
              <div style={{ width: '55px', height: '68px', border: '3px solid #9333ea', background: '#f3f4f6', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: '#9ca3af' }}>Photo</div>
            </div>
            <div style={{ padding: '8px 12px', fontSize: '7px' }}>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#666', width: '40%', display: 'inline-block' }}>Name</span>: <strong>Student Name</strong></div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#666', width: '40%', display: 'inline-block' }}>Father</span>: Mr. XYZ</div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#666', width: '40%', display: 'inline-block' }}>Course</span>: B.Tech</div>
              <div style={{ padding: '2px 0' }}><span style={{ color: '#666', width: '40%', display: 'inline-block' }}>Session</span>: 2024-25</div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(135deg, #9333ea, #c026d3)', padding: '5px 10px', display: 'flex', justifyContent: 'space-between', color: 'white', fontSize: '6px' }}>
              <span>📞 {emergencyContact}</span>
              <span>Valid: 2028</span>
            </div>
          </div>
        );

      case 'red-corporate':
        return (
          <div style={{ width: '192px', height: '288px', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui' }}>
            <div style={{ background: '#dc2626', padding: '10px', textAlign: 'center', clipPath: 'polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)', paddingBottom: '20px' }}>
              <div style={{ color: 'white', fontSize: '10px', fontWeight: '700' }}>{collegeName}</div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '6px' }}>CBSE Affiliated</div>
            </div>
            <div style={{ marginTop: '-15px', textAlign: 'center', position: 'relative', zIndex: 10 }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '50%', border: '3px solid #dc2626', background: '#f3f4f6', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: '#9ca3af' }}>Photo</div>
            </div>
            <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700', color: '#1e293b', margin: '6px 0' }}>Student Name</div>
            <div style={{ textAlign: 'center', fontSize: '7px', color: '#dc2626', marginBottom: '6px' }}>Class XII - Science</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', padding: '0 10px', fontSize: '6px' }}>
              <div style={{ background: '#fef2f2', padding: '4px', borderRadius: '3px' }}><div style={{ color: '#dc2626', fontWeight: '500' }}>ID No</div><div style={{ fontWeight: '600' }}>2024001</div></div>
              <div style={{ background: '#fef2f2', padding: '4px', borderRadius: '3px' }}><div style={{ color: '#dc2626', fontWeight: '500' }}>DOB</div><div style={{ fontWeight: '600' }}>10/08/05</div></div>
              <div style={{ background: '#fef2f2', padding: '4px', borderRadius: '3px' }}><div style={{ color: '#dc2626', fontWeight: '500' }}>Blood</div><div style={{ fontWeight: '600' }}>A+</div></div>
              <div style={{ background: '#fef2f2', padding: '4px', borderRadius: '3px' }}><div style={{ color: '#dc2626', fontWeight: '500' }}>Session</div><div style={{ fontWeight: '600' }}>2024-25</div></div>
            </div>
            <svg viewBox="0 0 192 20" style={{ position: 'absolute', bottom: 0, width: '100%', height: '20px' }}>
              <path d="M0,12 Q48,0 96,12 T192,12 L192,20 L0,20 Z" fill="#dc2626"/>
            </svg>
          </div>
        );

      case 'navy-gold':
        return (
          <div style={{ width: '192px', height: '288px', background: 'linear-gradient(180deg, #1e3a5f 0%, #1e40af 100%)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui', color: 'white' }}>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }}></div>
            <div style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ width: '28px', height: '28px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%', margin: '0 auto 4px', border: '1px solid #fbbf24', overflow: 'hidden' }}>
                {collegeLogo ? <img src={collegeLogo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : null}
              </div>
              <div style={{ fontSize: '9px', fontWeight: '700' }}>{collegeName}</div>
              <div style={{ color: '#fbbf24', fontSize: '6px' }}>OF TECHNOLOGY</div>
            </div>
            <div style={{ width: '50px', height: '62px', border: '2px solid white', background: 'rgba(255,255,255,0.1)', margin: '10px auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', opacity: '0.6' }}>Photo</div>
            <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>Student Name</div>
            <div style={{ textAlign: 'center', background: '#fbbf24', color: '#1e3a5f', fontSize: '6px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px', width: 'fit-content', margin: '4px auto' }}>STU-NIT-2024</div>
            <div style={{ padding: '6px 12px', fontSize: '6px' }}>
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.8' }}>Program</span><span>B.Tech</span></div>
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.8' }}>Batch</span><span>2024-28</span></div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fbbf24', padding: '4px', textAlign: 'center', color: '#1e3a5f', fontSize: '5px', fontWeight: '600' }}>Excellence • Innovation • Leadership</div>
          </div>
        );

      case 'green-modern':
        return (
          <div style={{ width: '192px', height: '288px', background: 'linear-gradient(180deg, #064e3b 0%, #059669 100%)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui', color: 'white' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: '40px', height: '40px', background: '#fbbf24', clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}></div>
            <div style={{ padding: '12px', textAlign: 'center' }}>
              <div style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%', margin: '0 auto 6px', overflow: 'hidden' }}>
                {collegeLogo ? <img src={collegeLogo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : null}
              </div>
              <div style={{ fontSize: '9px', fontWeight: '700' }}>{collegeName}</div>
              <div style={{ fontSize: '6px', opacity: '0.9' }}>Nurturing Future Leaders</div>
            </div>
            <div style={{ width: '50px', height: '62px', border: '2px solid #fbbf24', background: 'rgba(255,255,255,0.1)', margin: '4px auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', opacity: '0.6' }}>Photo</div>
            <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700', marginBottom: '6px' }}>Student Name</div>
            <div style={{ padding: '0 12px', fontSize: '6px' }}>
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.85' }}>Student No</span><span>GVS2024</span></div>
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.85' }}>Course</span><span>Class X</span></div>
              <div style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.85' }}>Session</span><span>2024-25</span></div>
            </div>
            <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)' }}>
              <div style={{ width: '35px', height: '35px', background: 'white', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '6px', color: '#059669' }}>QR</div>
            </div>
          </div>
        );

      case 'maroon-classic':
        return (
          <div style={{ width: '192px', height: '288px', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui', border: '2px solid #7c2d12' }}>
            <div style={{ background: '#7c2d12', padding: '8px', textAlign: 'center' }}>
              <div style={{ color: 'white', fontSize: '9px', fontWeight: '700' }}>{collegeName}</div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '5px' }}>Faith • Knowledge • Service</div>
            </div>
            <div style={{ height: '3px', background: 'linear-gradient(90deg, #fbbf24, #7c2d12, #fbbf24)' }}></div>
            <div style={{ padding: '10px' }}>
              <div style={{ width: '50px', height: '62px', border: '2px solid #7c2d12', background: '#f3f4f6', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: '#9ca3af' }}>Photo</div>
              <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700', color: '#7c2d12', marginBottom: '6px' }}>Student Name</div>
              <div style={{ fontSize: '6px' }}>
                <div style={{ borderBottom: '1px solid #eee', padding: '3px 0', display: 'flex' }}><span style={{ color: '#7c2d12', fontWeight: '500', width: '50%' }}>Admission No</span><span>SMC2024</span></div>
                <div style={{ borderBottom: '1px solid #eee', padding: '3px 0', display: 'flex' }}><span style={{ color: '#7c2d12', fontWeight: '500', width: '50%' }}>Class</span><span>IX-C</span></div>
                <div style={{ borderBottom: '1px solid #eee', padding: '3px 0', display: 'flex' }}><span style={{ color: '#7c2d12', fontWeight: '500', width: '50%' }}>DOB</span><span>18/09/2010</span></div>
              </div>
            </div>
            <div style={{ position: 'absolute', bottom: '-2px', left: '-2px', right: '-2px', background: '#7c2d12', padding: '4px', color: 'white', textAlign: 'center', fontSize: '5px' }}>Faith • Knowledge • Service</div>
          </div>
        );

      case 'pink-gradient':
        return (
          <div style={{ width: '192px', height: '288px', background: 'linear-gradient(180deg, #be185d 0%, #ec4899 50%, #f9a8d4 100%)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui', color: 'white' }}>
            <div style={{ padding: '10px', textAlign: 'center' }}>
              <div style={{ width: '28px', height: '28px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', margin: '0 auto 4px', overflow: 'hidden' }}>
                {collegeLogo ? <img src={collegeLogo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : null}
              </div>
              <div style={{ fontSize: '9px', fontWeight: '700' }}>{collegeName}</div>
              <div style={{ fontSize: '6px', opacity: '0.9' }}>Where Dreams Take Flight</div>
            </div>
            <div style={{ width: '50px', height: '62px', border: '2px solid white', borderRadius: '6px', background: 'rgba(255,255,255,0.15)', margin: '4px auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', opacity: '0.7' }}>Photo</div>
            <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>Student Name</div>
            <div style={{ background: 'rgba(255,255,255,0.15)', margin: '6px 10px', padding: '6px', borderRadius: '6px', fontSize: '6px' }}>
              <div style={{ padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.9' }}>ID</span><span>BIS2024</span></div>
              <div style={{ padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.9' }}>Grade</span><span>7th</span></div>
              <div style={{ padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: '0.9' }}>Blood</span><span>B-</span></div>
            </div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.2)', padding: '4px', textAlign: 'center', fontSize: '5px' }}>📞 Emergency: {emergencyContact}</div>
          </div>
        );

      case 'dark-premium':
        return (
          <div style={{ width: '192px', height: '288px', background: 'linear-gradient(180deg, #18181b 0%, #27272a 100%)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'system-ui', color: 'white', border: '1px solid #fbbf24' }}>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }}></div>
            <div style={{ padding: '10px', textAlign: 'center' }}>
              <div style={{ color: '#fbbf24', fontSize: '9px', fontWeight: '700' }}>{collegeName}</div>
              <div style={{ fontSize: '6px', opacity: '0.7' }}>Premium Education Institute</div>
            </div>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)' }}></div>
            <div style={{ width: '50px', height: '62px', border: '1px solid #fbbf24', background: 'rgba(255,255,255,0.05)', margin: '8px auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', opacity: '0.5' }}>Photo</div>
            <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>Student Name</div>
            <div style={{ textAlign: 'center', color: '#fbbf24', fontSize: '6px' }}>EA-PREM-2024</div>
            <div style={{ padding: '6px 12px', fontSize: '6px' }}>
              <div style={{ borderBottom: '1px solid rgba(251,191,36,0.2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#fbbf24' }}>Program</span><span>IB Diploma</span></div>
              <div style={{ borderBottom: '1px solid rgba(251,191,36,0.2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#fbbf24' }}>Year</span><span>2024-26</span></div>
              <div style={{ borderBottom: '1px solid rgba(251,191,36,0.2)', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#fbbf24' }}>Blood</span><span>A-</span></div>
            </div>
            <div style={{ position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '28px', height: '28px', background: 'white', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#18181b' }}>QR</div>
              <div style={{ textAlign: 'center' }}>
                {principalSign ? <img src={principalSign} style={{ height: '14px', filter: 'brightness(0) invert(1)' }} alt="" /> : <div style={{ width: '35px', borderTop: '1px solid #fbbf24', marginBottom: '2px' }}></div>}
                <div style={{ fontSize: '5px', color: '#fbbf24' }}>{principalName}</div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div style={{ width: '192px', height: '288px', background: 'linear-gradient(180deg, #1e40af 0%, #3b82f6 100%)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', fontFamily: 'system-ui', color: 'white', textAlign: 'center', padding: '15px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', marginBottom: '10px' }}>{collegeName}</div>
            <div style={{ width: '50px', height: '62px', border: '2px solid white', background: 'rgba(255,255,255,0.1)', margin: '10px auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px' }}>Photo</div>
            <div style={{ fontSize: '10px', fontWeight: '700' }}>Student Name</div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Front Side Preview */}
      <div>
        <p className="text-sm text-gray-500 mb-2 font-medium">Front Side</p>
        <div className="flex justify-center">
          {renderFrontPreview()}
        </div>
      </div>

      {/* Back Side Preview */}
      <div>
        <p className="text-sm text-gray-500 mb-2 font-medium">Back Side</p>
        <div className="w-48 h-72 rounded-lg shadow-xl overflow-hidden mx-auto bg-white border">
          <div className="p-3 h-full flex flex-col text-gray-800 text-[8px]">
            {/* Address */}
            <div className="mb-2">
              <p className="font-bold text-[9px] text-gray-500">ADDRESS</p>
              <p className="text-[7px]">{settings.college_address || 'College address will appear here'}</p>
            </div>
            
            {/* Emergency Contact */}
            <div className="mb-2">
              <p className="font-bold text-[9px] text-gray-500">EMERGENCY</p>
              <p className="text-[7px]">{settings.emergency_contact || '1800-XXX-XXXX'}</p>
            </div>
            
            {/* Rules */}
            <div className="flex-1">
              <p className="font-bold text-[9px] text-gray-500">RULES</p>
              <div className="text-[6px] space-y-0.5">
                {settings.rules_regulations?.split('\n').slice(0, 4).map((rule, i) => (
                  <p key={i} className="truncate">{rule}</p>
                ))}
              </div>
            </div>
            
            {/* QR Code */}
            {settings.show_qr_code && (
              <div className="mt-auto flex justify-center">
                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                  <QrCode className="h-8 w-8 text-gray-400" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ICardSettingsNew = () => {
  const [settings, setSettings] = useState({
    selected_template: 'blue-curve',
    college_name: '',
    college_address: '',
    college_logo: '',
    principal_signature: '',
    principal_name: '',
    emergency_contact: '',
    rules_regulations: '1. This card is non-transferable.\n2. Report loss immediately.\n3. Card must be carried at all times.\n4. Misuse will result in disciplinary action.',
    validity_years: 4,
    show_qr_code: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/icard-presets/settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/icard-presets/upload-image?image_type=${imageType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (imageType === 'logo') {
        setSettings(prev => ({ ...prev, college_logo: response.data.image_url }));
      } else {
        setSettings(prev => ({ ...prev, principal_signature: response.data.image_url }));
      }
      toast.success(`${imageType === 'logo' ? 'Logo' : 'Signature'} uploaded successfully!`);
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/icard-presets/settings', settings);
      toast.success('I-Card settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-7 w-7 text-blue-600" />
          I-Card Settings
        </h1>
        <p className="text-gray-600 mt-1">Configure your institution's ID card design and settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Settings */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Template Selection */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-600" />
              Select Template
              <span className="ml-2 text-xs font-normal text-gray-500 bg-purple-100 px-2 py-0.5 rounded-full">8 Professional Designs</span>
            </h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {PROFESSIONAL_TEMPLATES.map(template => (
                <TemplatePreviewCard
                  key={template.id}
                  template={template}
                  isSelected={settings.selected_template === template.id}
                  onClick={() => setSettings(prev => ({ ...prev, selected_template: template.id }))}
                  settings={settings}
                />
              ))}
            </div>
          </div>

          {/* College Information */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5 text-gray-600" />
              College Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  College/Institute Name
                </label>
                <input
                  type="text"
                  value={settings.college_name}
                  onChange={e => setSettings(prev => ({ ...prev, college_name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter college name"
                  data-testid="college-name-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  College Address (for back side)
                </label>
                <textarea
                  value={settings.college_address}
                  onChange={e => setSettings(prev => ({ ...prev, college_address: e.target.value }))}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter complete address"
                  data-testid="college-address-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Number
                </label>
                <input
                  type="text"
                  value={settings.emergency_contact}
                  onChange={e => setSettings(prev => ({ ...prev, emergency_contact: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 1800-XXX-XXXX"
                  data-testid="emergency-contact-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Principal Name
                </label>
                <input
                  type="text"
                  value={settings.principal_name}
                  onChange={e => setSettings(prev => ({ ...prev, principal_name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Principal's full name"
                  data-testid="principal-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Validity (Years)
                </label>
                <select
                  value={settings.validity_years}
                  onChange={e => setSettings(prev => ({ ...prev, validity_years: parseInt(e.target.value) }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="validity-years-select"
                >
                  {[1, 2, 3, 4, 5, 6].map(y => (
                    <option key={y} value={y}>{y} Year{y > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_qr_code}
                    onChange={e => setSettings(prev => ({ ...prev, show_qr_code: e.target.checked }))}
                    className="rounded text-blue-600 focus:ring-blue-500"
                    data-testid="show-qr-checkbox"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Show QR Code on back
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Image Uploads */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-green-600" />
              Upload Images
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* College Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  College Logo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  {settings.college_logo ? (
                    <div className="space-y-2">
                      <img 
                        src={settings.college_logo} 
                        alt="College Logo" 
                        className="h-20 mx-auto object-contain"
                      />
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, college_logo: '' }))}
                        className="text-sm text-red-600 hover:text-red-800"
                        data-testid="remove-logo-btn"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload logo</p>
                      <p className="text-xs text-gray-400">PNG, JPG (max 2MB)</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => handleImageUpload(e, 'logo')}
                        className="hidden"
                        data-testid="logo-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Principal Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Principal Signature
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  {settings.principal_signature ? (
                    <div className="space-y-2">
                      <img 
                        src={settings.principal_signature} 
                        alt="Signature" 
                        className="h-16 mx-auto object-contain"
                      />
                      <button
                        onClick={() => setSettings(prev => ({ ...prev, principal_signature: '' }))}
                        className="text-sm text-red-600 hover:text-red-800"
                        data-testid="remove-signature-btn"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload signature</p>
                      <p className="text-xs text-gray-400">PNG with transparent bg preferred</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => handleImageUpload(e, 'signature')}
                        className="hidden"
                        data-testid="signature-upload-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Rules & Regulations */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              Rules & Regulations (Back Side)
            </h2>
            
            <textarea
              value={settings.rules_regulations}
              onChange={e => setSettings(prev => ({ ...prev, rules_regulations: e.target.value }))}
              rows={5}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="Enter rules (one per line)"
              data-testid="rules-textarea"
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Save className="h-5 w-5" />
              )}
              Save Settings
            </button>
          </div>
        </div>

        {/* Right Column - Preview */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Live Preview
            </h2>
            
            <LivePreview template={settings.selected_template} settings={settings} />

            <div className="mt-6 pt-4 border-t">
              <p className="text-sm text-gray-500 text-center">
                Go to <strong className="text-blue-600">Admission → Print I-Card (Pro)</strong> to print student cards
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ICardSettingsNew;
